import React, { useEffect, useState, useRef } from 'react';

const ResponsiveLanding = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const sectionRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Intersection Observer for visibility
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.2
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    // Add resize event listener
    window.addEventListener('resize', handleResize);

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Responsive style calculations
  const getResponsiveStyle = (mobileStyle, desktopStyle) => {
    return windowWidth < 768 ? mobileStyle : desktopStyle;
  };

  const styles = {
    mainContainer: {
      display: 'flex',
      flexDirection: getResponsiveStyle('column', 'row'),
      padding: getResponsiveStyle('20px', '40px'),
      backgroundColor: '#ffffff',
      minHeight: '500px',
      fontFamily: 'Arial, sans-serif',
      width: '100%',
      maxWidth: '1400px',
      margin: '0 auto',
      boxSizing: 'border-box',
      overflowX: 'hidden',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
      transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
    },
    textSection: {
      flex: 1,
      paddingRight: getResponsiveStyle('0', '40px'),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      marginBottom: getResponsiveStyle('40px', '0'),
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateX(0)' : 'translateX(-50px)',
      transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
      transitionDelay: '0.2s'
    },
    heading: {
      fontSize: getResponsiveStyle('32px', '52px'),
      color: '#400684',
      fontWeight: 'bold',
      fontFamily: '"Oswald", sans-serif',
      marginBottom: '20px',
      lineHeight: '1.2',
      textAlign: getResponsiveStyle('center', 'left')
    },
    paragraph: {
      fontSize: getResponsiveStyle('14px', '18px'),
      lineHeight: '1.6',
      color: 'black',
      marginBottom: '30px',
      textAlign: getResponsiveStyle('center', 'left')
    },
    statsContainer: {
      position: 'relative',
      flex: 1,
      minHeight: getResponsiveStyle('300px', '400px'),
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateX(0)' : 'translateX(50px)',
      transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
      transitionDelay: '0.4s'
    },
    backgroundImage: {
      width: '100%',
      maxWidth: getResponsiveStyle('300px', '400px'),
      height: 'auto',
      objectFit: 'contain',
      position: 'relative',
      zIndex: 1
    },
    statCard: (position, index) => {
      const baseStyles = {
        position: 'absolute',
        padding: getResponsiveStyle('8px 12px', '12px 16px'),
        backgroundColor: 'white',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.18)',
        zIndex: 2,
        transform: `scale(${getResponsiveStyle(0.8, 1)}) translateY(${isVisible ? '0' : '20px'})`,
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
        transitionDelay: `${0.6 + (index * 0.2)}s`
      };

      const positionStyles = {
        top: {
          top: getResponsiveStyle('5%', '10%'),
          right: getResponsiveStyle('5%', '10%')
        },
        middle: {
          top: '40%',
          right: getResponsiveStyle('2%', '5%')
        },
        bottom: {
          bottom: getResponsiveStyle('10%', '20%'),
          left: getResponsiveStyle('5%', '10%')
        }
      };

      return {
        ...baseStyles,
        ...positionStyles[position]
      };
    },
    icon: {
      width: '25px',
      height: '25px',
      backgroundColor: '#400684',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '14px'
    },
    statContent: {
      display: 'flex',
      flexDirection: 'column'
    },
    statValue: {
      fontWeight: 'bold',
      fontSize: getResponsiveStyle('12px', '14px'),
      color: '#333'
    },
    statLabel: {
      fontSize: getResponsiveStyle('10px', '12px'),
      color: '#666'
    },
    purpleText: {
      color: 'black',
      fontFamily: '"Oswald", sans-serif'
    }
  };

  return (
    <div ref={sectionRef} style={styles.mainContainer}>
      <div style={styles.textSection}>
        <h1 style={styles.heading}>
          MOBILITY
          <span style={styles.purpleText}> & </span>
          5G INTERNET
        </h1>
        <p style={styles.paragraph}>
          Experience lightning-fast internet solutions with our trusted ISP services. 
          As an authorized reseller for AT&T, Verizon, and Spectrum, we provide DIA & FIA 
          5G Internet and IPVPN solutions. Enjoy speeds ranging from 200 Mbps to 1 Gbps 
          with over 99.9% network reliability. Stay connected even during power outages 
          with a wireless LTE modem and automatic internet backup for up to 4 devices. 
          Your seamless connectivity starts here!
        </p>
      </div>

      <div style={styles.statsContainer}>
        <img 
          src="/server.webp"
          alt="Server Stack" 
          style={styles.backgroundImage}
        />
        
        {/* Stats Cards */}
        <div style={styles.statCard('top', 0)}>
          <div style={styles.icon}>⚡</div>
          <div style={styles.statContent}>
            <div style={styles.statValue}>200 Mbps - 1 Gbps</div>
            <div style={styles.statLabel}>Speed Range</div>
          </div>
        </div>

        <div style={styles.statCard('middle', 1)}>
          <div style={styles.icon}>✔</div>
          <div style={styles.statContent}>
            <div style={styles.statValue}>99.9%</div>
            <div style={styles.statLabel}>Network Reliability</div>
          </div>
        </div>

        <div style={styles.statCard('bottom', 2)}>
          <div style={styles.icon}>📶</div>
          <div style={styles.statContent}>
            <div style={styles.statValue}>Wireless LTE</div>
            <div style={styles.statLabel}>Stay Connected During Outages</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponsiveLanding;